/**
 * @file: service-record.service.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */
import { HttpClient } from '@euler/lib/request';
import {
  MediaAlbum,
  MediaAlbumByYearMonth,
  MediaWithSource,
  VehicleServiceRecord,
  VehicleServiceRecordProjection,
} from '@euler/model';
import { ResourceAccessScope } from '@euler/model/enum';
import { MediaSourceType } from '@euler/model/report';
import { WithExtraVehicleModelProps } from '@euler/model/WithExtraVehicleModelProps';
import type { VehicleMediaSearchParams } from '@euler/services/vehicle-info.service';
import { formatDateTime } from '@euler/utils/formatters';

export type ServiceRecordSearchParams = {
  scope?: ResourceAccessScope;
  startDate?: string | Date;
  endDate?: string | Date;
  startInclusive?: boolean;
  endInclusive?: boolean;
  licensePlateNo?: string;
  vin?: string;
  keyword?: string;
  tag?: string;
  serviceAgentId?: number;
  authorId?: number;
  isMainRecord?: boolean;
  parentOrderNo?: string;
  orderNo?: string;
};

export class ServiceRecordService {
  constructor(private readonly api: HttpClient) {}

  async getVehicleServiceRecordCount(
    params: Omit<ServiceRecordSearchParams, 'licensePlateNo' | 'vin'> & {
      licensePlateNo: string;
      vin: string;
    },
  ): Promise<number> {
    return await this.api
      .get()
      .url('/service-records/count', {
        start_date: params.startDate
          ? formatDateTime(params.startDate, 'yyyy-MM-dd HH:mm:ss')
          : undefined,
        end_date: params.endDate
          ? formatDateTime(params.endDate, 'yyyy-MM-dd HH:mm:ss')
          : undefined,
        start_inclusive: params.startInclusive,
        end_inclusive: params.endInclusive,
        plateno: params.licensePlateNo,
        vin: params.vin,
        keyword: params.keyword,
        service_agent_id: params.serviceAgentId,
        author_id: params.authorId,
      })
      .future();
  }

  async getServiceRecordById(
    id: string,
    args?: {
      items?: boolean;
      events?: boolean;
      pendingIssues?: boolean;
      troubleCodes?: boolean;
      comment?: boolean;
    },
  ): Promise<WithExtraVehicleModelProps<VehicleServiceRecord>> {
    return await this.api
      .get()
      .url('/service-records/:id', {
        id,
        items: args?.items,
        events: args?.events,
        pending_issues: args?.pendingIssues,
        trouble_codes: args?.troubleCodes,
        comment: args?.comment,
      })
      .future();
  }

  async search(
    params: ServiceRecordSearchParams,
    projection: VehicleServiceRecordProjection | null | undefined,
    offset: number,
    limit: number,
  ): Promise<WithExtraVehicleModelProps<VehicleServiceRecord>[]> {
    return await this.api
      .get()
      .url('/service-records/search', {
        start_date: params.startDate
          ? formatDateTime(params.startDate, 'yyyy-MM-dd HH:mm:ss')
          : undefined,
        end_date: params.endDate
          ? formatDateTime(params.endDate, 'yyyy-MM-dd HH:mm:ss')
          : undefined,
        start_inclusive: params.startInclusive,
        end_inclusive: params.endInclusive,
        plateno: params.licensePlateNo,
        vin: params.vin,
        keyword: params.keyword,
        service_agent_id: params.serviceAgentId,
        author_id: params.authorId,
        scope: params.scope,
        items: projection?.items,
        events: projection?.events,
        pending_issues: projection?.pendingIssues,
        trouble_codes: projection?.troubleCodes,
        comment: projection?.comment,
        is_main_record: params.isMainRecord,
        parent_order_no: params.parentOrderNo,
        order_no: params.orderNo,
        tag: params.tag,
        offset,
        limit,
      })
      .future();
  }

  async getMediaAlbumsByMileage(params: {
    licensePlateNo: string;
    vin: string;
    sources?: MediaSourceType[];
    scope?: ResourceAccessScope;
  }): Promise<MediaAlbum<number>[]> {
    return await this.api
      .get()
      .url('/service-records/medias/albums/by-mileage', {
        ...params,
        sources:
          params.sources != null && params.sources.length > 0
            ? params.sources.join(',')
            : undefined,
      })
      .future();
  }

  async getMediaAlbumsByYearMonth(params: {
    licensePlateNo: string;
    vin: string;
    sources?: MediaSourceType[];
    scope?: ResourceAccessScope;
  }): Promise<MediaAlbumByYearMonth[]> {
    return await this.api
      .get()
      .url('/service-records/medias/albums/by-year-month', {
        ...params,
        sources:
          params.sources != null && params.sources.length > 0
            ? params.sources.join(',')
            : undefined,
      })
      .future();
  }

  async searchMedias(
    params: VehicleMediaSearchParams,
  ): Promise<MediaWithSource[]> {
    return await this.api
      .get()
      .url('/service-records/medias/search', {
        ...params,
        sources:
          params.sources != null && params.sources.length > 0
            ? params.sources.join(',')
            : undefined,
      })
      .future();
  }
}
